import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "./ToolbarTooltipButton";
import copyIcon from "../../assets/posts/copy.svg";

// ── Color math ────────────────────────────────────────────────────────────────
const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const f = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(f, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r, g, b) {
  const t = n => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${t(r)}${t(g)}${t(b)}`.toUpperCase();
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h, s: mx === 0 ? 0 : d / mx, v: mx };
}

function hsvToRgb(h, s, v) {
  const c = v * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

const hexToHsv = hex => { const { r, g, b } = hexToRgb(hex); return rgbToHsv(r, g, b); };
const isValidHex = hex => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
const rgbObjToString = ({ r, g, b }) => `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;

function parseRgbInput(str) {
  const parts = str.replace(/[^\d,]/g, "").split(",").map(Number);
  if (parts.length === 3 && parts.every(v => !isNaN(v) && v >= 0 && v <= 255))
    return { r: parts[0], g: parts[1], b: parts[2] };
  return null;
}

function relLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return [r, g, b].reduce((acc, c, i) => {
    c /= 255;
    c = c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return acc + c * [0.2126, 0.7152, 0.0722][i];
  }, 0);
}

function contrastRatio(a, b) {
  const L1 = relLuminance(a), L2 = relLuminance(b);
  const [hi, lo] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (hi + 0.05) / (lo + 0.05);
}

const readableOn = hex => relLuminance(hex) > 0.4 ? "#111111" : "#FFFFFF";

// ── SV Square ─────────────────────────────────────────────────────────────────
function SVSquare({ h, s, v, onChange }) {
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX, clientY) => {
    const rect = ref.current.getBoundingClientRect();
    onChange(clamp((clientX - rect.left) / rect.width, 0, 1), clamp(1 - (clientY - rect.top) / rect.height, 0, 1));
  }, [onChange]);

  return (
    <div
      ref={ref}
      className="relative w-full h-40 rounded-xl cursor-crosshair overflow-hidden touch-none"
      style={{ background: `hsl(${h}, 100%, 50%)` }}
      onPointerDown={e => { dragging.current = true; ref.current.setPointerCapture(e.pointerId); update(e.clientX, e.clientY); }}
      onPointerMove={e => { if (dragging.current) update(e.clientX, e.clientY); }}
      onPointerUp={e => { dragging.current = false; try { ref.current.releasePointerCapture(e.pointerId); } catch (_) {} }}
    >
      <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #fff, rgba(255,255,255,0))" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #000, rgba(0,0,0,0))" }} />
      <div
        className="absolute w-4 h-4 rounded-full border-2 border-white pointer-events-none"
        style={{
          left: `${s * 100}%`, top: `${(1 - v) * 100}%`,
          transform: "translate(-50%, -50%)",
          background: rgbToHex(...Object.values(hsvToRgb(h, s, v)).map(Math.round)),
          boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
        }}
      />
    </div>
  );
}

// ── Hue Slider ────────────────────────────────────────────────────────────────
function HueSlider({ h, onChange }) {
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = useCallback(clientX => {
    const rect = ref.current.getBoundingClientRect();
    onChange(clamp((clientX - rect.left) / rect.width, 0, 1) * 360);
  }, [onChange]);

  return (
    <div
      ref={ref}
      className="relative w-full h-5 rounded-full cursor-pointer touch-none mt-3"
      style={{ background: "linear-gradient(to right,#f00 0%,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,#f00 100%)" }}
      onPointerDown={e => { dragging.current = true; ref.current.setPointerCapture(e.pointerId); update(e.clientX); }}
      onPointerMove={e => { if (dragging.current) update(e.clientX); }}
      onPointerUp={e => { dragging.current = false; try { ref.current.releasePointerCapture(e.pointerId); } catch (_) {} }}
    >
      <div
        className="absolute w-5 h-5 rounded-full border-2 border-white pointer-events-none"
        style={{
          left: `${(h / 360) * 100}%`, top: "50%",
          transform: "translate(-50%, -50%)",
          background: `hsl(${h}, 100%, 50%)`,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
        }}
      />
    </div>
  );
}

// ── Mini Preview ──────────────────────────────────────────────────────────────
function MiniPreview({ colors, t }) {
  return (
    <div className="rounded-xl p-4 border border-gray-200 mb-4 transition-all duration-200" style={{ background: colors.canvas }}>
      <p className="text-xs font-bold mb-1 opacity-50" style={{ color: colors.structure }}>
        {t("brandColors.preview.label")}
      </p>
      <p className="text-sm font-bold mb-1.5" style={{ color: colors.structure }}>
        {t("brandColors.preview.heading")}
      </p>
      <p className="text-xs mb-3 opacity-70 leading-relaxed" style={{ color: colors.structure }}>
        {t("brandColors.preview.body")}
      </p>
      <span
        className="inline-block text-xs font-bold px-3 py-1.5 rounded-lg transition-all duration-200"
        style={{ background: colors.accent, color: readableOn(colors.accent) }}
      >
        {t("brandColors.preview.cta")}
      </span>
    </div>
  );
}

// ── Contrast Checker ──────────────────────────────────────────────────────────
function ContrastChecker({ colors, t }) {
  const textOnBg = contrastRatio(colors.structure, colors.canvas);
  const accentOnBg = contrastRatio(colors.accent, colors.canvas);

  const checks = [
    {
      label: t("brandColors.contrast.textOnBg"),
      ratio: textOnBg, fg: colors.structure, bg: colors.canvas,
      levels: [{ name: "AA", threshold: 4.5 }, { name: "AAA", threshold: 7 }],
    },
    {
      label: t("brandColors.contrast.accentOnBg"),
      ratio: accentOnBg, fg: colors.accent, bg: colors.canvas,
      levels: [{ name: "AA Large", threshold: 3 }, { name: "AA", threshold: 4.5 }],
    },
  ];

  return (
    <div className="mt-4 space-y-2">
      <p className="text-xs font-bold uppercase tracking-wide text-black-400 mb-2">
        {t("brandColors.contrast.title")}
      </p>
      {checks.map(({ label, ratio, fg, bg, levels }) => (
        <div key={label} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-gray-200">
          <div
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: bg, color: fg }}
          >Aa</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-black-800">{label}</p>
            <p className="text-xs text-black-400 font-mono">{ratio.toFixed(2)}:1</p>
          </div>
          <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
            {levels.map(({ name, threshold }) => {
              const pass = ratio >= threshold;
              return (
                <span
                  key={name}
                  className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                  }`}
                >
                  {name} {pass ? "✓" : "✗"}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Config ────────────────────────────────────────────────────────────────────
const COLOR_ROLES = [
  { key: "canvas",    tabKey: "brandColors.tabCanvas" },
  { key: "structure", tabKey: "brandColors.tabStructure" },
  { key: "accent",    tabKey: "brandColors.tabAccent" },
];

// ── BrandColorPicker ──────────────────────────────────────────────────────────
export default function BrandColorPicker({ colors, onChange }) {
  const { t } = useTranslation();
  const [active, setActive] = useState("canvas");
  const [hexDraft, setHexDraft] = useState("");
  const [rgbDraft, setRgbDraft] = useState("");
  const [copied, setCopied] = useState(false);

  const safeColors = {
    canvas:    colors?.canvas    || "#FFFFFF",
    structure: colors?.structure || "#111111",
    accent:    colors?.accent    || "#04EA77",
  };

  useEffect(() => {
    const val = safeColors[active];
    setHexDraft(val);
    setRgbDraft(rgbObjToString(hexToRgb(val)));
  }, [active, colors]);

  const [localHsv, setLocalHsv] = useState(() => hexToHsv(safeColors[active]));

  // Sync only when switching tabs
  useEffect(() => {
    setLocalHsv(hexToHsv(safeColors[active]));
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const setHsv = (partial) => {
    const next = { ...localHsv, ...partial };
    setLocalHsv(next);
    const { r, g, b } = hsvToRgb(next.h, next.s, next.v);
    onChange({ ...safeColors, [active]: rgbToHex(r, g, b) });
  };

  const applyHex = (val) => {
    setHexDraft(val);
    const norm = val.startsWith("#") ? val : `#${val}`;
    if (isValidHex(norm)) {
      const upper = norm.toUpperCase();
      setRgbDraft(rgbObjToString(hexToRgb(upper)));
      setLocalHsv(hexToHsv(upper)); // ← sync HSV from typed hex
      onChange({ ...safeColors, [active]: upper });
    }
  };

  const applyRgb = (val) => {
    setRgbDraft(val);
    const parsed = parseRgbInput(val);
    if (parsed) {
      const hex = rgbToHex(parsed.r, parsed.g, parsed.b);
      setHexDraft(hex);
      setLocalHsv(hexToHsv(hex)); // ← sync HSV from typed RGB
      onChange({ ...safeColors, [active]: hex });
    }
  };

  const copyHex = async () => {
    try {
      await navigator.clipboard.writeText(safeColors[active]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_) {}
  };

  return (
    <div className="flex flex-col">
      {/* Preview */}
      <MiniPreview colors={safeColors} t={t} />

      {/* Tabs */}
      <div className="flex gap-1.5">
        {COLOR_ROLES.map(({ key, tabKey }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActive(key)}
            className={`flex-1 flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-t-xl border transition-all ${
              active === key
                ? "bg-white border-gray-200 border-b-white text-black-800 -mb-px relative z-10"
                : "bg-black-10 border-transparent text-black-400 hover:text-black-600"
            }`}
          >
            <span
              className="w-4 h-4 rounded-md border border-gray-200 flex-shrink-0"
              style={{ background: safeColors[key] }}
            />
            <span className="text-xs font-bold">{t(tabKey)}</span>
          </button>
        ))}
      </div>

      {/* Picker panel */}
      <div className="bg-white border border-gray-200 rounded-b-xl rounded-tr-xl p-4 space-y-3" style={{ borderTop: "2px solid #04ea77" }}>
        <SVSquare h={localHsv.h} s={localHsv.s} v={localHsv.v} onChange={(s, v) => setHsv({ s, v })} />
        <HueSlider h={localHsv.h} onChange={h => setHsv({ h })} />

        {/* HEX */}
        <div className="mt-2">
          <label className="text-xs font-bold uppercase tracking-wide text-black-400 block mb-1">HEX</label>
          <div className="flex gap-2">
            <div className="w-9 h-9 rounded-lg border border-gray-200 flex-shrink-0 transition-all" style={{ background: safeColors[active] }} />
            <input
              type="text"
              value={hexDraft}
              onChange={e => applyHex(e.target.value)}
              maxLength={7}
              placeholder="#FFFFFF"
              className="border rounded px-3 py-2 w-full outline-none focus:ring-2 focus:ring-primary-300 font-mono text-sm"
            />
            <ToolbarTooltipButton
              label="Copy hex"
              variant="iconSm"
              className="!mt-0 flex-shrink-0"
              onClick={copyHex}
            >
              {copied
                ? <span className="text-xs font-bold text-success-500">✓</span>
                : <img src={copyIcon} alt="" className="w-4 h-4" />}
            </ToolbarTooltipButton>
          </div>
        </div>

        {/* RGB */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-black-400 block mb-1">RGB</label>
          <input
            type="text"
            value={rgbDraft}
            onChange={e => applyRgb(e.target.value)}
            placeholder="255, 255, 255"
            className="border rounded px-3 py-2 w-full outline-none focus:ring-2 focus:ring-primary-300 font-mono text-sm"
          />
        </div>
      </div>

      {/* Contrast checker */}
      <ContrastChecker colors={safeColors} t={t} />
    </div>
  );
}